function _createForOfIteratorHelper(o, allowArrayLike) { var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"]; if (!it) { if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; var F = function F() {}; return { s: F, n: function n() { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }, e: function e(_e) { throw _e; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var normalCompletion = true, didErr = false, err; return { s: function s() { it = it.call(o); }, n: function n() { var step = it.next(); normalCompletion = step.done; return step; }, e: function e(_e2) { didErr = true; err = _e2; }, f: function f() { try { if (!normalCompletion && it.return != null) it.return(); } finally { if (didErr) throw err; } } }; }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

/**
 * @license Copyright (c) 2003-2021, CKSource - Frederico Knabben. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */

/**
 * @module engine/model/operation/utils
 */
import Node from '../node';
import Text from '../text';
import TextProxy from '../textproxy';
import Range from '../range';
import DocumentFragment from '../documentfragment';
import NodeList from '../nodelist';
import CKEditorError from '@ckeditor/ckeditor5-utils/src/ckeditorerror';
/**
 * Contains functions used for composing model tree by {@link module:engine/model/operation/operation~Operation operations}.
 * Those functions are built on top of {@link module:engine/model/node~Node node}, and it's child classes', APIs.
 *
 * @protected
 * @namespace utils
 */

/**
 * Inserts given nodes at given position.
 *
 * @protected
 * @function module:engine/model/operation/utils~utils.insert
 * @param {module:engine/model/position~Position} position Position at which nodes should be inserted.
 * @param {module:engine/model/node~NodeSet} nodes Nodes to insert.
 * @returns {module:engine/model/range~Range} Range spanning over inserted elements.
 */

export function _insert(position, nodes) {
  nodes = _normalizeNodes(nodes); // We have to count offset before inserting nodes because they can get merged and we would get wrong offsets.

  var offset = nodes.reduce(function (sum, node) {
    return sum + node.offsetSize;
  }, 0);
  var parent = position.parent; // Insertion might be in a text node, we should split it if that's the case.

  _splitNodeAtPosition(position);

  var index = position.index; // Insert nodes at given index. After splitting we have a proper index and insertion is between nodes,
  // using basic `Element` API.

  parent._insertChild(index, nodes); // Merge text nodes, if possible. Merging is needed only at points where inserted nodes "touch" "old" nodes.


  _mergeNodesAtIndex(parent, index + nodes.length);

  _mergeNodesAtIndex(parent, index);

  return new Range(position, position.getShiftedBy(offset));
}
/**
 * Removed nodes in given range. Only {@link module:engine/model/range~Range#isFlat flat} ranges are accepted.
 *
 * @protected
 * @function module:engine/model/operation/utils~utils._remove
 * @param {module:engine/model/range~Range} range Range containing nodes to remove.
 * @returns {Array.<module:engine/model/node~Node>}
 */

export function _remove(range) {
  if (!range.isFlat) {
    /**
     * Trying to remove a range which starts and ends in different element.
     *
     * @error operation-utils-remove-range-not-flat
     */
    throw new CKEditorError('operation-utils-remove-range-not-flat', this);
  }

  var parent = range.start.parent; // Range may be inside text nodes, we have to split them if that's the case.

  _splitNodeAtPosition(range.start);

  _splitNodeAtPosition(range.end); // Remove the text nodes using basic `Element` API.


  var removed = parent._removeChildren(range.start.index, range.end.index - range.start.index); // Merge text nodes, if possible. After some nodes were removed, node before and after removed range will be
  // touching at the position equal to the removed range beginning. We check merging possibility there.


  _mergeNodesAtIndex(parent, range.start.index);

  return removed;
}
/**
 * Moves nodes in given range to given target position. Only {@link module:engine/model/range~Range#isFlat flat} ranges are accepted.
 *
 * @protected
 * @function module:engine/model/operation/utils~utils.move
 * @param {module:engine/model/range~Range} sourceRange Range containing nodes to move.
 * @param {module:engine/model/position~Position} targetPosition Position to which nodes should be moved.
 * @returns {module:engine/model/range~Range} Range containing moved nodes.
 */

export function _move(sourceRange, targetPosition) {
  if (!sourceRange.isFlat) {
    /**
     * Trying to move a range which starts and ends in different element.
     *
     * @error operation-utils-move-range-not-flat
     */
    throw new CKEditorError('operation-utils-move-range-not-flat', this);
  }

  var nodes = _remove(sourceRange); // We have to fix `targetPosition` because model changed after nodes from `sourceRange` got removed and
  // that change might have an impact on `targetPosition`.


  targetPosition = targetPosition._getTransformedByDeletion(sourceRange.start, sourceRange.end.offset - sourceRange.start.offset);
  return _insert(targetPosition, nodes);
}
/**
 * Sets given attribute on nodes in given range. The attributes are only set on top-level nodes of the range, not on its children.
 *
 * @protected
 * @function module:engine/model/operation/utils~utils._setAttribute
 * @param {module:engine/model/range~Range} range Range containing nodes that should have the attribute set. Must be a flat range.
 * @param {String} key Key of attribute to set.
 * @param {*} value Attribute value.
 */

export function _setAttribute(range, key, value) {
  // Range might start or end in text nodes, so we have to split them.
  _splitNodeAtPosition(range.start);

  _splitNodeAtPosition(range.end); // Iterate over all items in the range.


  var _iterator = _createForOfIteratorHelper(range.getItems({
    shallow: true
  })),
      _step;

  try {
    for (_iterator.s(); !(_step = _iterator.n()).done;) {
      var item = _step.value;
      // Iterator will return `TextProxy` instances but we know that those text proxies will
      // always represent full text nodes (this is guaranteed thanks to splitting we did before).
      // So, we can operate on those text proxies' text nodes.
      var node = item.is('$textProxy') ? item.textNode : item;

      if (value !== null) {
        node._setAttribute(key, value);
      } else {
        node._removeAttribute(key);
      } // After attributes changing it may happen that some text nodes can be merged. Try to merge with previous node.


      _mergeNodesAtIndex(node.parent, node.index);
    } // Try to merge last changed node with it's previous sibling (not covered by the loop above).

  } catch (err) {
    _iterator.e(err);
  } finally {
    _iterator.f();
  }

  _mergeNodesAtIndex(range.end.parent, range.end.index);
}
/**
 * Normalizes given object or an array of objects to an array of {@link module:engine/model/node~Node nodes}. See
 * {@link module:engine/model/node~NodeSet NodeSet} for details on how normalization is performed.
 *
 * @protected
 * @function module:engine/model/operation/utils~utils.normalizeNodes
 * @param {module:engine/model/node~NodeSet} nodes Objects to normalize.
 * @returns {Array.<module:engine/model/node~Node>} Normalized nodes.
 */

export function _normalizeNodes(nodes) {
  var normalized = [];

  if (!(nodes instanceof Array)) {
    nodes = [nodes];
  } // Convert instances of classes other than Node.


  for (var i = 0; i < nodes.length; i++) {
    if (typeof nodes[i] == 'string') {
      normalized.push(new Text(nodes[i]));
    } else if (nodes[i] instanceof TextProxy) {
      normalized.push(new Text(nodes[i].data, nodes[i].getAttributes()));
    } else if (nodes[i] instanceof DocumentFragment || nodes[i] instanceof NodeList) {
      var _iterator2 = _createForOfIteratorHelper(nodes[i]),
          _step2;

      try {
        for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
          var child = _step2.value;
          normalized.push(child);
        }
      } catch (err) {
        _iterator2.e(err);
      } finally {
        _iterator2.f();
      }
    } else if (nodes[i] instanceof Node) {
      normalized.push(nodes[i]);
    } // Skip unrecognized type.

  } // Merge text nodes.


  for (var _i = 1; _i < normalized.length; _i++) {
    var node = normalized[_i];
    var prev = normalized[_i - 1];

    if (node instanceof Text && prev instanceof Text && _haveSameAttributes(node, prev)) {
      // Doing this instead changing `prev.data` because `data` is readonly.
      normalized.splice(_i - 1, 2, new Text(prev.data + node.data, prev.getAttributes()));
      _i--;
    }
  }

  return normalized;
} // Checks if nodes before and after given index in given element are {@link module:engine/model/text~Text text nodes} and
// merges them into one node if they have same attributes.
//
// Merging is done by removing two text nodes and inserting a new text node containing data from both merged text nodes.
//
// @private
// @param {module:engine/model/element~Element} element Parent element of nodes to merge.
// @param {Number} index Index between nodes to merge.

function _mergeNodesAtIndex(element, index) {
  var nodeBefore = element.getChild(index - 1);
  var nodeAfter = element.getChild(index); // Check if both of those nodes are text objects with same attributes.

  if (nodeBefore && nodeAfter && nodeBefore.is('$text') && nodeAfter.is('$text') && _haveSameAttributes(nodeBefore, nodeAfter)) {
    // Append text of text node after index to the before one.
    var mergedNode = new Text(nodeBefore.data + nodeAfter.data, nodeBefore.getAttributes()); // Remove separate text nodes.

    element._removeChildren(index - 1, 2); // Insert merged text node.


    element._insertChild(index - 1, mergedNode);
  }
} // Checks if given position is in a text node, and if so, splits the text node in two text nodes, each of them
// containing a part of original text node.
//
// @private
// @param {module:engine/model/position~Position} position Position at which node should be split.


function _splitNodeAtPosition(position) {
  var textNode = position.textNode;
  var element = position.parent;

  if (textNode) {
    var offsetDiff = position.offset - textNode.startOffset;
    var index = textNode.index;

    element._removeChildren(index, 1);

    var firstPart = new Text(textNode.data.substr(0, offsetDiff), textNode.getAttributes());
    var secondPart = new Text(textNode.data.substr(offsetDiff), textNode.getAttributes());

    element._insertChild(index, [firstPart, secondPart]);
  }
} // Checks whether two given nodes have same attributes.
//
// @private
// @param {module:engine/model/node~Node} nodeA Node to check.
// @param {module:engine/model/node~Node} nodeB Node to check.
// @returns {Boolean} `true` if nodes have same attributes, `false` otherwise.


function _haveSameAttributes(nodeA, nodeB) {
  var iteratorA = nodeA.getAttributes();
  var iteratorB = nodeB.getAttributes();

  var _iterator3 = _createForOfIteratorHelper(iteratorA),
      _step3;

  try {
    for (_iterator3.s(); !(_step3 = _iterator3.n()).done;) {
      var attr = _step3.value;

      if (attr[1] !== nodeB.getAttribute(attr[0])) {
        return false;
      }

      iteratorB.next();
    }
  } catch (err) {
    _iterator3.e(err);
  } finally {
    _iterator3.f();
  }

  return iteratorB.next().done;
}
/**
 * Value that can be normalized to an array of {@link module:engine/model/node~Node nodes}.
 *
 * Non-arrays are normalized as follows:
 * * {@link module:engine/model/node~Node Node} is left as is,
 * * {@link module:engine/model/textproxy~TextProxy TextProxy} and `String` are normalized to {@link module:engine/model/text~Text Text},
 * * {@link module:engine/model/nodelist~NodeList NodeList} is normalized to an array containing all nodes that are in that node list,
 * * {@link module:engine/model/documentfragment~DocumentFragment DocumentFragment} is normalized to an array containing all of it's
 * * children.
 *
 * Arrays are processed item by item like non-array values and flattened to one array. Normalization always results in
 * a flat array of {@link module:engine/model/node~Node nodes}. Consecutive text nodes (or items normalized to text nodes) will be
 * merged if they have same attributes.
 *
 * @typedef {module:engine/model/node~Node|module:engine/model/textproxy~TextProxy|String|
 * module:engine/model/nodelist~NodeList|module:engine/model/documentfragment~DocumentFragment|Iterable}
 * module:engine/model/node~NodeSet
 */